import React from 'react';
import { Helmet } from 'react-helmet-async';
import './styles.css';

export default function Home() {
  // Structured data for the homepage
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Raffcodes",
    "description": "IT solutions and full-stack development services in Nairobi, Kenya.",
    "url": "https://raffcodes.tech/",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://raffcodes.tech/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Raffcodes | IT Solutions & Full-Stack Development in Nairobi</title>
        <meta name="description" content="Raffcodes provides professional IT solutions, full-stack development, system setup, and tech support in Nairobi | Kajiado, Kenya. From idea to interface - we build it." />
        <meta name="keywords" content="IT solutions Nairobi, full-stack development Kenya, web development, system setup, tech support, Raffcodes, software development, React developer" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://raffcodes.tech/" />

        {/* Open Graph */}
        <meta property="og:title" content="Raffcodes | IT Solutions & Full-Stack Development" />
        <meta property="og:description" content="Professional IT solutions, full-stack development, system setup, and tech support in Nairobi & Kajiado, Kenya." />
        <meta property="og:url" content="https://raffcodes.tech/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://raffcodes.tech/images/raffcodes-og.jpg" />
        <meta property="og:site_name" content="Raffcodes" />
        <meta property="og:locale" content="en_KE" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Raffcodes | IT Solutions & Full-Stack Development" />
        <meta name="twitter:description" content="From idea to interface - professional IT solutions and development services in Kenya." />
        <meta name="twitter:image" content="https://raffcodes.tech/images/raffcodes-og.jpg" />
        <meta name="twitter:site" content="@raffcodes" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="page home">
        {/* Hero Section - Tech Professional Style */}
        <section className="hero">
          <div className="hero-overlay">
            <div className="hero-badge">
              <span className="badge-pulse">●</span>
              <span>Available for Projects</span>
            </div>
            
            <div className="hero-content">
              <div className="hero-logo-wrapper">
                <img 
                  src="/images/raffcodes-logo.png" 
                  alt="Raffcodes IT Solutions Logo" 
                  className="logo" 
                  loading="eager"
                />
              </div>
              
              <h1>
                <span className="gradient-text">From Idea</span>
                <span className="text-divider">→</span>
                <span className="gradient-text">Interface</span>
              </h1>
              <p className="hero-subtitle">Full-Stack Development · IT Solutions · Tech Support</p>
              
              <div className="tech-stack-tags">
                <span className="tech-tag">React</span>
                <span className="tech-tag">Node.js</span>
                <span className="tech-tag">Python</span>
                <span className="tech-tag">AWS</span>
                <span className="tech-tag">Docker</span>
              </div>
              
              <p className="hero-description">
                From system setup to full-stack development, Raffcodes delivers trusted IT support, 
                troubleshooting, and custom web solutions. Whether you're upgrading Windows, 
                deploying a web app, or securing your network, we make technology work for you.
              </p>
              
              <div className="cta-container">
                <a href="/contact" className="cta primary">
                  <i className="fas fa-rocket"></i>
                  Start Your Project
                </a>
                <a href="/services" className="cta secondary">
                  <i className="fas fa-code"></i>
                  View Services
                </a>
              </div>

              <div className="stats-container">
                <div className="stat-item">
                  <span className="stat-number">50+</span>
                  <span className="stat-label">Projects Delivered</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <span className="stat-number">98%</span>
                  <span className="stat-label">Client Satisfaction</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <span className="stat-number">24/7</span>
                  <span className="stat-label">Tech Support</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Preview - Tech Professional Style */}
        <section className="services-preview">
          <div className="container">
            <div className="section-header">
              <span className="section-tag">What We Do</span>
              <h2>IT Solutions & Development</h2>
              <p className="section-subtitle">
                End-to-end technology services for businesses and individuals
              </p>
            </div>

            <div className="services-grid">
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon">
                    <i className="fas fa-laptop-code"></i>
                  </div>
                </div>
                <h3>Full-Stack Development</h3>
                <p>Custom web applications built with React, Node.js, and modern frameworks. Scalable, performant, and user-focused.</p>
                <div className="service-tech">
                  <span>React</span>
                  <span>Node.js</span>
                  <span>MongoDB</span>
                </div>
                <a href="/services/web-development" className="service-link">
                  Learn More <i className="fas fa-arrow-right"></i>
                </a>
              </div>

              <div className="service-card featured">
                <div className="featured-badge">Popular</div>
                <div className="service-icon-wrapper">
                  <div className="service-icon">
                    <i className="fas fa-server"></i>
                  </div>
                </div>
                <h3>System Setup & Maintenance</h3>
                <p>Windows installation, Office setup, driver updates, and ongoing IT support. Keep your systems running smoothly.</p>
                <div className="service-tech">
                  <span>Windows</span>
                  <span>Linux</span>
                  <span>Networking</span>
                </div>
                <a href="/services/system-setup" className="service-link">
                  Learn More <i className="fas fa-arrow-right"></i>
                </a>
              </div>

              <div className="service-card">
                <div className="service-icon-wrapper">
                  <div className="service-icon">
                    <i className="fas fa-tools"></i>
                  </div>
                </div>
                <h3>Troubleshooting & Repair</h3>
                <p>Quick diagnosis and resolution of hardware and software issues. Get back to work with minimal downtime.</p>
                <div className="service-tech">
                  <span>Hardware</span>
                  <span>Software</span>
                  <span>Network</span>
                </div>
                <a href="/services/troubleshooting" className="service-link">
                  Learn More <i className="fas fa-arrow-right"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Trusted By / Client Logos */}
        <section className="trust-section">
          <div className="container">
            <p className="trust-label">Trusted by businesses and individuals across Kenya</p>
            <div className="trust-logos">
              <span className="trust-item">Startups</span>
              <span className="trust-item">SMEs</span>
              <span className="trust-item">NGOs</span>
              <span className="trust-item">Freelancers</span>
              <span className="trust-item">E-commerce</span>
            </div>
          </div>
        </section>

        {/* CTA Banner with Image */}
        <div className="cta-banner">
          <div className="cta-banner-content">
            <div className="cta-text">
              <h3>Ready to Transform Your Technology?</h3>
              <p>Get a free consultation and let's build something amazing together.</p>
            </div>
            <a href="/contact" className="cta primary large">
              <i className="fas fa-paper-plane"></i>
              Get in Touch
            </a>
          </div>
        </div>
      </div>
    </>
  );
}